import { Box, Heading, Link, Text, useColorModeValue } from '@chakra-ui/react';

type AccentTone = 'primary' | 'green';

type ScoreExplainerSectionLiteProps = {
  id?: string;
  eyebrow?: string;
  accentTone?: AccentTone;
  heading?: string;
  subheading?: string;
  body?: string;
  analogy?: string;
  footnote?: string;
  learnMoreHref?: string;
  learnMoreLabel?: string;
  /** Editorial card style for homepage (left accent, gradient). */
  blendTop?: boolean;
};

function accentTokens(tone: AccentTone, mode: 'light' | 'dark') {
  const map = {
    primary: {
      border: mode === 'light' ? 'primary.500' : 'primary.400',
      eyebrow: mode === 'light' ? 'primary.600' : 'primary.300',
      subheading: mode === 'light' ? 'primary.600' : 'primary.300',
      calloutBg: mode === 'light' ? 'primary.50' : 'whiteAlpha.50',
      calloutBorder: mode === 'light' ? 'primary.100' : 'whiteAlpha.100',
      gradient:
        'linear-gradient(135deg, rgba(10,112,185,0.14) 0%, transparent 55%)',
    },
    green: {
      border: mode === 'light' ? 'green.500' : 'green.400',
      eyebrow: mode === 'light' ? 'green.700' : 'green.300',
      subheading: mode === 'light' ? 'green.700' : 'green.300',
      calloutBg: mode === 'light' ? 'green.50' : 'rgba(34, 84, 61, 0.35)',
      calloutBorder: mode === 'light' ? 'green.200' : 'green.700',
      gradient:
        'linear-gradient(135deg, rgba(72,187,120,0.16) 0%, transparent 55%)',
    },
  };
  return map[tone];
}

export function ScoreExplainerSectionLite({
  id,
  eyebrow,
  accentTone = 'primary',
  heading = 'Meet WCS',
  subheading = 'The WagerWatchdog Credibility Score',
  body = 'WCS measures how skilled an expert is at picking winners—consistency, sample size, and historical win-rate quality. It is not the same as profitability.',
  analogy = 'Think of WCS as a credit score for betting expertise.',
  footnote = 'Higher WCS = stronger long-term picking skill.',
  learnMoreHref,
  learnMoreLabel = 'How we calculate WCS',
  blendTop = false,
}: ScoreExplainerSectionLiteProps) {
  const accentLight = accentTokens(accentTone, 'light');
  const accentDark = accentTokens(accentTone, 'dark');
  const panelBg = useColorModeValue(
    'white',
    blendTop ? 'blackAlpha.400' : 'gray.800',
  );
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const accentBorder = useColorModeValue(accentLight.border, accentDark.border);
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue(
    accentLight.subheading,
    accentDark.subheading,
  );
  const bodyColor = useColorModeValue('gray.600', 'gray.400');
  const analogyColor = useColorModeValue('gray.800', 'gray.200');
  const footnoteColor = useColorModeValue('gray.500', 'gray.500');
  const eyebrowColor = useColorModeValue(
    accentLight.eyebrow,
    accentDark.eyebrow,
  );
  const calloutBg = useColorModeValue(
    accentLight.calloutBg,
    accentDark.calloutBg,
  );
  const calloutBorder = useColorModeValue(
    accentLight.calloutBorder,
    accentDark.calloutBorder,
  );
  const linkColor = useColorModeValue('primary.600', 'primary.300');
  const gradient = useColorModeValue(accentLight.gradient, accentDark.gradient);

  return (
    <Box
      id={id}
      w="100%"
      h={blendTop ? '100%' : undefined}
      p={{ base: 5, md: 8 }}
      bg={panelBg}
      borderRadius="16px"
      border="1px solid"
      borderColor={panelBorder}
      borderLeftWidth={blendTop ? '4px' : '1px'}
      borderLeftColor={blendTop ? accentBorder : panelBorder}
      boxShadow={
        blendTop
          ? '0 24px 48px -20px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.04)'
          : 'sm'
      }
      textAlign={blendTop ? 'left' : 'center'}
      sx={blendTop ? { backgroundImage: gradient } : undefined}
    >
      {blendTop && eyebrow ? (
        <Text
          fontSize="xs"
          fontWeight="bold"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color={eyebrowColor}
          mb={3}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Heading
        as="h2"
        fontSize={{ base: '2xl', md: '3xl' }}
        fontWeight="extrabold"
        color={headingColor}
        mb={2}
        textAlign={blendTop ? 'left' : 'center'}
      >
        {heading}
      </Heading>
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        fontWeight="semibold"
        color={subheadingColor}
        mb={{ base: 4, md: 5 }}
        textAlign={blendTop ? 'left' : 'center'}
      >
        {subheading}
      </Text>
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color={bodyColor}
        lineHeight="1.7"
        maxW={blendTop ? 'none' : '640px'}
        mx={blendTop ? 0 : 'auto'}
        mb={4}
        textAlign={blendTop ? 'left' : 'center'}
      >
        {body}
      </Text>
      <Box
        px={{ base: 4, md: 5 }}
        py={{ base: 3, md: 4 }}
        borderRadius="12px"
        bg={calloutBg}
        borderWidth="1px"
        borderColor={calloutBorder}
        maxW={blendTop ? 'none' : '520px'}
        mx={blendTop ? 0 : 'auto'}
        mb={3}
      >
        <Text
          fontSize={{ base: 'md', md: 'lg' }}
          fontWeight="semibold"
          color={analogyColor}
          lineHeight="1.5"
          textAlign={blendTop ? 'left' : 'center'}
        >
          {analogy}
        </Text>
      </Box>
      <Text
        fontSize="sm"
        color={footnoteColor}
        textAlign={blendTop ? 'left' : 'center'}
        mb={learnMoreHref ? 3 : 0}
      >
        {footnote}
      </Text>
      {learnMoreHref ? (
        <Box textAlign={blendTop ? 'left' : 'center'}>
          <Link
            href={learnMoreHref}
            isExternal
            fontSize="sm"
            fontWeight="semibold"
            color={linkColor}
            display="inline-block"
            _hover={{ color: 'primary.400', textDecoration: 'underline' }}
          >
            {learnMoreLabel} →
          </Link>
        </Box>
      ) : null}
    </Box>
  );
}

/** @deprecated Use ScoreExplainerSectionLite */
export const MeetWcsSectionLite = ScoreExplainerSectionLite;
